import type { User } from "hooks/useAddUpdateUser";
import dayjs from "utils/dayjs";

export interface OnboardingOwnerInput {
  firstName: string;
  lastName: string;
  email: string;
  title?: string;
  phone?: string;
  birthdate?: number;
  address?: {
    addressName: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
  location?: { id: string };
  ownershipPercentage?: number;
  inviteToSignIn?: boolean;
}

// An owner keeps their id when their email is edited; only one added in this
// session has no id yet, and is matched by email until it gets one.
export const isSameProfile = (a: User, b: User): boolean =>
  a.id && b.id
    ? a.id === b.id
    : Boolean(a.email) &&
      a.email.toLowerCase() === (b.email ?? "").toLowerCase();

export const mergeUserProfiles = (
  existing: User[],
  incoming: User | User[],
): User[] => {
  const merged = [...existing];
  (Array.isArray(incoming) ? incoming : [incoming]).forEach((user) => {
    const index = merged.findIndex((profile) => isSameProfile(profile, user));
    if (index === -1) merged.push(user);
    else merged[index] = { ...merged[index], ...user };
  });
  return merged;
};

// Follows the current answer, since activateCompany sends every submitted user
// whose stored isOwner is true to the issuer as a beneficial owner.
export const beneficialOwnersFor = (
  profiles: User[],
  ownersRequired: boolean,
): User[] =>
  ownersRequired ? profiles.filter((profile) => profile.isOwner) : [];

export const profilesToSubmit = (
  authorizedPerson: User | undefined,
  beneficialOwners: User[],
): User[] =>
  [authorizedPerson, ...beneficialOwners]
    .filter((profile): profile is User => Boolean(profile))
    .filter(
      (profile, index, all) =>
        all.findIndex((other) => isSameProfile(other, profile)) === index,
    );

// A person already saved keeps their access, so only a new owner carries the
// choice to be invited to sign in.
export const toOnboardingOwnerInput = (owner: User): OnboardingOwnerInput => {
  const { address, ownershipPercentage } = owner;
  return {
    firstName: owner.firstName,
    lastName: owner.lastName,
    email: owner.email,
    ...(owner.title && { title: owner.title }),
    ...(owner.phone && { phone: owner.phone }),
    ...(owner.birthdate && { birthdate: dayjs(owner.birthdate).valueOf() }),
    ...(address?.address1 && {
      address: {
        addressName: address.addressName || "Default",
        address1: address.address1,
        ...(address.address2 && { address2: address.address2 }),
        city: address.city,
        state: address.state,
        zipcode: address.zipcode,
        country: address.country || "US",
      },
    }),
    ...(owner.location?.id && { location: { id: owner.location.id } }),
    ...(ownershipPercentage !== undefined &&
      ownershipPercentage !== "" && {
        ownershipPercentage: Number(ownershipPercentage),
      }),
    ...(!owner.id && { inviteToSignIn: Boolean(owner.inviteToSignIn) }),
  };
};
